import { useEffect, useState } from "react";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Stack } from "@twilio-paste/core/stack";
import { NotesIcon } from "@twilio-paste/icons/esm/NotesIcon";
import { HistoryIcon } from "@twilio-paste/icons/esm/HistoryIcon";
import { ProductHomeIcon } from "@twilio-paste/icons/esm/ProductHomeIcon";
import { styled } from "@twilio-paste/core/styling-library";

import {
  BaseChatAction,
  liveChatCopy,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  CsatSurvey,
  useSurveyContext,
} from "@twiliointernal/e2-commons-csat-survey";

import {
  ACTION_BTN,
  defaultDisabledState,
  DisabledButtonsState,
  EndedChatActionsProps,
} from "./definitions";

const EndedChatActionsContainer = styled(Box)`
  [data-paste-element="ACTION_BTN"] {
    margin-bottom: 8px;
  }
`;

/**
 * @NOTE
 * Current State
 *  - This component is specifically built for Live Chat usage in HCA.
 *  - Keeping all logics and definitions within this folder to easily move it out in case we want to
 *    de-couple from the "Ended Chat" experience and allow the consumer of <LiveChat /> to implement
 *    "Ended Chat" experience.
 * Future State
 *  - Add feature toggle on supported actions
 *  - We would like Live Chat should be app agnostic, but need to consider how much of freedom
 *    are we giving the consumer of LiveChat to build their own experience? For example:
 *    1. Should we only allow the consumer to only pass in the action handlers? or allow them
 *       to pass the whole component to customize the whole experience?
 *    2. Should we consider allowing the consumer to modify the assistant message?
 *    3. What should be the default state if the consumer doesn't customize "Ended Chat" experience?
 */
const EndedChatActions = ({
  ticketId,
  actions = {},
}: EndedChatActionsProps) => {
  const { navigateHome, navigateTicketDetails } = actions || {};
  const {
    state: { isSurveyEnabled, isSurveyVisible, isSurveySubmitted },
    handlers: { handleShowSurvey },
  } = useSurveyContext();
  const [disabledButtons, setDisabledButtons] =
    useState<DisabledButtonsState>(defaultDisabledState);

  const {
    isHomeButtonDisabled,
    isViewTicketButtonDisabled,
    isProvideFeedbackButtonDisabled,
  } = disabledButtons;

  useEffect(() => {
    if (isSurveySubmitted) {
      setDisabledButtons({
        isHomeButtonDisabled: false,
        isViewTicketButtonDisabled: false,
        isProvideFeedbackButtonDisabled: true,
      });
    }

    if (!isSurveyVisible && !isSurveySubmitted) {
      setDisabledButtons({
        isHomeButtonDisabled: false,
        isViewTicketButtonDisabled: false,
        isProvideFeedbackButtonDisabled: false,
      });
    }
  }, [isSurveySubmitted, isSurveyVisible]);

  const actionsToRender: BaseChatAction[] = [];

  // Add navigate to home action
  if (navigateHome) {
    actionsToRender.push(navigateHome);
  }

  // Add navigate to ticket details action
  if (navigateTicketDetails) {
    actionsToRender.push({
      ...navigateTicketDetails,
      handleActionButtonClick: () => {
        if (ticketId && navigateTicketDetails) {
          navigateTicketDetails.handleActionButtonClick(ticketId);
        }
      },
    });
  }

  // DEFAULT: Add CSAT action
  actionsToRender.push({
    label: liveChatCopy.endedActions.feedback,
    icon: <NotesIcon decorative />,
    disabled: isProvideFeedbackButtonDisabled,
    pressed: isSurveyVisible && !isSurveySubmitted,
    handleActionButtonClick: (): void => {
      handleShowSurvey();
      setDisabledButtons({
        ...disabledButtons,
        isHomeButtonDisabled: true,
        isViewTicketButtonDisabled: true,
      });
    },
  });

  return (
    <EndedChatActionsContainer as="section">
      <Stack orientation={["vertical", "horizontal"]} spacing="space30">
        {actionsToRender.map(
          ({
            disabled,
            icon,
            handleActionButtonClick,
            label,
            pressed,
            variant = "secondary",
          }) => {
            return (
              <Box marginTop={"space30"}>
                <Button
                  key={label}
                  variant={variant}
                  size="rounded_small"
                  onClick={handleActionButtonClick}
                  disabled={disabled}
                  pressed={pressed}
                  element={ACTION_BTN}
                >
                  {icon}
                  {label}
                </Button>
              </Box>
            );
          }
        )}
      </Stack>
      {((isSurveyEnabled && isSurveyVisible) || isSurveySubmitted) && (
        <CsatSurvey />
      )}
    </EndedChatActionsContainer>
  );
};

export default EndedChatActions;
